import { PayoffLeg } from "../../payoffContainer/livePayoffContainer"
import { CSSProperties, memo, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react"
import { PayoffGraphContext } from "../payoffGraphContext"
import { isEmbeddedModeSet } from "../../../util/general"
import { selectIsTouchEnabled } from "../../../selectors/device"
import { useSelector } from "react-redux"
import { alpha, useTheme } from "@mui/material"

export type PayoffOrderTagProps = {
    payoffLeg: PayoffLeg,
    readonly?: boolean,
    setHtmlElement: (element: HTMLDivElement) => void
}

const PayoffOrderTag: React.FC<PayoffOrderTagProps> = ({payoffLeg, setHtmlElement, readonly}) => {
    const { security, quantity, type } = payoffLeg;
    const payoffGraphContext = useContext(PayoffGraphContext);
    const isTouchEnabled = useSelector(selectIsTouchEnabled);
    const theme = useTheme()

    const startTime = useRef(0);
    const [isDragging, setIsDragging] = useState(false)
    const previewRef = useRef<HTMLDivElement>(null)

    const isLegReadOnly = useMemo(()=>{
        return (( payoffLeg.type === 'order' && payoffLeg.readonly) || (payoffLeg.type === 'position' && !!payoffLeg?.linkedOrders?.length)) || readonly
    }, [payoffLeg, readonly])

    // While an existing position is being dragged it stays in place; render it
    // faded to signal that the drop will create a new leg rather than move it.
    const showAsExisting = useMemo(
        () => isLegReadOnly || (type === 'position' && isDragging),
        [isLegReadOnly, type, isDragging]
    )

    const setDragCursor = useCallback((dragging: boolean) => {
        if (isLegReadOnly) return
        setIsDragging(dragging)
        document.body.style.cursor = dragging ? 'grabbing' : ''
    }, [isLegReadOnly])

    const style: CSSProperties = useMemo(() => ({
        position: "absolute",
        top: 0,
        transition: "transform 0.2s ease",
        transformOrigin: "top",
        transform: "translateX(-50%)",
        width: "30px",
        left: "-10000000px",
        height: "45px",
        cursor: isLegReadOnly ? "default" : isDragging ? "grabbing" : "grab"
    }), [isLegReadOnly, isDragging])

    const startX = useRef<number>(0);
    const dragListenersRef = useRef<{
        mousemove?: (e: MouseEvent) => void
        mouseup?: (e: MouseEvent) => void
        touchmove?: (e: TouchEvent) => void
        touchend?: (e: TouchEvent) => void
    }>({})

    const clearDragListeners = useCallback(() => {
        const listeners = dragListenersRef.current
        if (listeners.mousemove) {
            document.removeEventListener("mousemove", listeners.mousemove)
            listeners.mousemove = undefined
        }
        if (listeners.mouseup) {
            document.removeEventListener("mouseup", listeners.mouseup)
            listeners.mouseup = undefined
        }
        if (listeners.touchmove) {
            document.removeEventListener("touchmove", listeners.touchmove)
            listeners.touchmove = undefined
        }
        if (listeners.touchend) {
            document.removeEventListener("touchend", listeners.touchend)
            listeners.touchend = undefined
        }
    }, [])

    const endDrag = useCallback(() => {
        clearDragListeners()
        startX.current = 0
        setDragCursor(false)
    }, [clearDragListeners, setDragCursor])

    const isPositionLeg = type === 'position'

    // Stable ref callback so re-renders (e.g. on drag start/end) don't detach
    // and reattach the slider element, which would thrash the chart layout.
    const containerRef = useCallback((ele: HTMLDivElement | null) => {
        setHtmlElement(ele)
    }, [setHtmlElement])

    useEffect(() => {
        return () => {
            clearDragListeners()
            document.body.style.cursor = ''
        }
    }, [clearDragListeners])

    const onMouseDown = useCallback((e: React.MouseEvent) => {
        const chart = payoffGraphContext.payoffChart
        if (!chart) return

        startTime.current = Date.now();
        startX.current = e.clientX;
        setDragCursor(true)
        chart.onSliderMoveStart(payoffLeg.id);

        const onMouseUp = () => {
            endDrag()
            payoffGraphContext.payoffChart?.onSliderMoveEnd(payoffLeg.id)
        }
        const onMouseMove = (e: MouseEvent) => {
            const delta = e.clientX - startX.current
            if (isPositionLeg && previewRef.current) previewRef.current.style.transform = `translateX(${delta}px)`
            payoffGraphContext.payoffChart?.onSliderMove(payoffLeg.id, delta)
        }

        clearDragListeners()
        dragListenersRef.current.mousemove = onMouseMove
        dragListenersRef.current.mouseup = onMouseUp
        document.addEventListener("mouseup", onMouseUp);
        document.addEventListener("mousemove", onMouseMove);
        
    }, [payoffGraphContext, payoffLeg, setDragCursor, endDrag, clearDragListeners, isPositionLeg])

    const onTouchStart = useCallback((e: React.TouchEvent) => {
        if (!isEmbeddedModeSet()) return;
        const chart = payoffGraphContext.payoffChart
        if (!chart) return

        startTime.current = Date.now();
        startX.current = e.touches[0].clientX;
        setDragCursor(true)
        chart.onSliderMoveStart(payoffLeg.id);

        const onTouchEnd = () => {
            endDrag()
            payoffGraphContext.payoffChart?.onSliderMoveEnd(payoffLeg.id)
        }
        const onTouchMove = (e: TouchEvent) => {
            const delta = e.touches[0].clientX - startX.current
            if (isPositionLeg && previewRef.current) previewRef.current.style.transform = `translateX(${delta}px)`
            payoffGraphContext.payoffChart?.onSliderMove(payoffLeg.id, delta)
        }

        clearDragListeners()
        dragListenersRef.current.touchmove = onTouchMove
        dragListenersRef.current.touchend = onTouchEnd
        document.addEventListener("touchend", onTouchEnd);
        document.addEventListener("touchmove", onTouchMove);
        
    }, [payoffGraphContext, payoffLeg, setDragCursor, endDrag, clearDragListeners, isPositionLeg])

    const legColor = quantity > 0 ? theme.palette.buy.main : theme.palette.sell.main

    const renderPin = (faded: boolean, withLock: boolean) => (
        <svg xmlns="http://www.w3.org/2000/svg" width="30"
            height="40"
            viewBox="0 0 110 160" fill="none">

            <line x1={55} x2={55} y1={-68.97} y2={1.38} stroke={alpha(legColor, 0.4)} strokeWidth={5} strokeDasharray={`${10},${3}`} />
            <path fillRule="evenodd" strokeDasharray={6}
                stroke={faded ? legColor : 'none'}
                strokeWidth={faded ? 4 : 0}
                fill={alpha(legColor, faded ? 0.55 : 0.7)}
                fillOpacity="1"
                clipRule="evenodd" d="M100 157H11C10.4477 157 10 156.552 10 156V50.3064C10 49.7834 10.2048 49.2813 10.5706 48.9075L54.7853 3.73024C55.1775 3.32951 55.8225 3.32951 56.2147 3.73024L100.429 48.9075C100.795 49.2813 101 49.7834 101 50.3064V156C101 156.552 100.552 157 100 157Z" />
            <text className="font-feature-num" fill="#fff" x={55}
                y={withLock ? 135 : 115}
                fontWeight={500} fontSize={50} textAnchor="middle">
                {Math.abs(quantity)/(security.lotSize?? 1)}
            </text>
            {
                withLock ?
                    <path d="M58.8 56.9474V53.4737H52.2V56.9474H58.8ZM42.3 62.1579V77.7895C42.3 78.7447 43.0425 79.5263 43.95 79.5263H67.05C67.9575 79.5263 68.7 78.7447 68.7 77.7895V62.1579C68.7 61.2026 67.9575 60.4211 67.05 60.4211H43.95C43.0425 60.4211 42.3 61.2026 42.3 62.1579ZM68.7 56.9474C70.5315 56.9474 72 58.4932 72 60.4211V79.5263C72 81.4542 70.5315 83 68.7 83H42.3C40.4685 83 39 81.4542 39 79.5263L39.0165 60.4211C39.0165 58.4932 40.4685 56.9474 42.3 56.9474H48.9V53.4737C48.9 51.5458 50.3685 50 52.2 50H58.8C60.6315 50 62.1 51.5458 62.1 53.4737V56.9474H68.7Z" fill="#fff" />
                    : null
            }
        </svg>
    )

    return  <div style={style} ref={containerRef} onMouseDown={isLegReadOnly ? undefined : onMouseDown}
        onTouchStart={isLegReadOnly || !isTouchEnabled ? undefined : onTouchStart}>
                {renderPin(showAsExisting, isPositionLeg)}
                {isPositionLeg && isDragging ? (
                    <div ref={previewRef} style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        transform: "translateX(0px)",
                        pointerEvents: "none"
                    }}>
                        {renderPin(false, false)}
                    </div>
                ) : null}
            </div>
}

export default memo(PayoffOrderTag);